import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  FormControlLabel,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useMemo, type ReactElement } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "react-query";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router";
import styled from "styled-components";
import {
  PROFILES_EXPORT_REPORTS,
  useCreateProfilesListReport,
} from "../../../../api/reports";
import BackdropLoader from "../../../../shared/components/backdrop-loader/BackdropLoader";
import Button from "../../../../shared/components/button/Button";
import { useSnackbar } from "../../../../shared/components/snackbar/Snackbar";
import { DialogHeader } from "../../../../shared/styles/common-styles";
import type { ProblemDetails } from "../../../../shared/types/problem-details.type";
import type { RootState } from "../../../../store/store";
import { ReportConflict } from "../../../generate-reports/report.constant";
import type { ExportProfilesFormValues } from "../../../generate-reports/report.types";
import {
  validateFileName,
  type FileValidationResults,
} from "../../../../utils/file-utilities/file-utils";
import { getProblemDetailsErrorMessage } from "../../../../utils/problem-details-utilities/problem-details-utils";
import {
  areAllOptionalSelectionsChecked,
  areSomeOptionalSelectionsChecked,
  buildExportIncludesSummaryKeys,
  buildProfilesListReportPayload,
  DEFAULT_EXPORT_PROFILES_FORM_VALUES,
} from "./exportProfilesModal.utils";

const OptionDescription = styled(Typography)`
  display: block;
  color: #666666;
  margin-left: 2rem;
  margin-bottom: 0.5rem;
`;

const ExportIncludesSummary = styled(Box)`
  margin-top: 1.5rem;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  background-color: #e8f4fc;
`;

const ExportIncludesLabel = styled(Typography).attrs({
  component: "span",
  variant: "body2",
})`
  font-weight: 700;
  color: #0077ff;
`;

const ExportIncludesValue = styled(Typography).attrs({
  component: "span",
  variant: "body2",
})`
  color: #1976d2;
`;

const FileNameField = styled(Box)`
  margin-top: 1.5rem;
`;

interface ExportProfilesModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const ExportProfilesModal = ({
  isOpen,
  onClose,
}: ExportProfilesModalProps): ReactElement => {
  const { t: translate } = useTranslation(["profile", "reports", "common"]);
  const teamId = useSelector((state: RootState) => state.permissions.teamId);
  const createProfilesListReport = useCreateProfilesListReport();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();

  const {
    control,
    handleSubmit,
    reset,
    setError,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<ExportProfilesFormValues>({
    mode: "all",
    defaultValues: DEFAULT_EXPORT_PROFILES_FORM_VALUES,
  });

  const watchedValues = watch();
  const isKeyPartiesChecked = watchedValues.keyParties;
  const fileName = watchedValues.fileName;

  useEffect(() => {
    if (!isKeyPartiesChecked && watchedValues.keyPartySearches) {
      setValue("keyPartySearches", false);
    }
  }, [isKeyPartiesChecked, setValue, watchedValues.keyPartySearches]);

  const handleClose = useCallback((): void => {
    reset(DEFAULT_EXPORT_PROFILES_FORM_VALUES);
    onClose();
  }, [onClose, reset]);

  const handleSelectAllChange = useCallback(
    (checked: boolean): void => {
      setValue("profileSearches", checked);
      setValue("keyParties", checked);
      setValue("keyPartySearches", checked);
    },
    [setValue]
  );

  const selectAllChecked = areAllOptionalSelectionsChecked(watchedValues);
  const selectAllIndeterminate =
    areSomeOptionalSelectionsChecked(watchedValues) && !selectAllChecked;

  const onSubmit = async (data: ExportProfilesFormValues): Promise<void> => {
    if (!teamId) {
      showSnackbar(
        translate("profile:exportProfiles.errors.teamUnavailable"),
        true
      );
      return;
    }

    try {
      const payload = buildProfilesListReportPayload(data, teamId);
      await createProfilesListReport.mutateAsync(payload);
      await queryClient.invalidateQueries(PROFILES_EXPORT_REPORTS);
      handleClose();
      navigate("/protect/reports/profiles");
    } catch (error) {
      const problemDetails = error as ProblemDetails;

      if (problemDetails?.details?.type === ReportConflict) {
        const errorMsg = translate(
          "reports:validationMessages.fileNameAlreadyExist",
          { payload: { fileName: data.fileName } }
        );

        setError("fileName", {
          type: "server",
          message: errorMsg,
        });
        return;
      }

      showSnackbar(
        getProblemDetailsErrorMessage(
          problemDetails,
          translate("profile:exportProfiles.errors.exportFailed")
        ),
        true
      );
    }
  };

  const exportIncludesSummary = useMemo(() => {
    return buildExportIncludesSummaryKeys(watchedValues)
      .map((key) => translate(`profile:exportProfiles.exportIncludes.${key}`))
      .join(", ");
  }, [translate, watchedValues]);

  const optionalOptions = useMemo(
    () => [
      {
        name: "profileSearches" as const,
        label: translate(
          "profile:exportProfiles.options.profileSearches.label"
        ),
        description: translate(
          "profile:exportProfiles.options.profileSearches.description"
        ),
        disabled: false,
      },
      {
        name: "keyParties" as const,
        label: translate("profile:exportProfiles.options.keyParties.label"),
        description: translate(
          "profile:exportProfiles.options.keyParties.description"
        ),
        disabled: false,
      },
      {
        name: "keyPartySearches" as const,
        label: translate(
          "profile:exportProfiles.options.keyPartySearches.label"
        ),
        description: translate(
          "profile:exportProfiles.options.keyPartySearches.description"
        ),
        disabled: !isKeyPartiesChecked,
      },
    ],
    [isKeyPartiesChecked, translate]
  );

  return (
    <Dialog open={isOpen} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogHeader aria-label="export-profiles-modal-title">
        {translate("profile:exportProfiles.modal.title")}
        <IconButton
          aria-label={translate("common:close")}
          onClick={handleClose}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogHeader>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {translate("profile:exportProfiles.modal.scopeNote")}
          </Typography>

          <FormControlLabel
            control={
              <Checkbox
                checked={selectAllChecked}
                indeterminate={selectAllIndeterminate}
                onChange={(event) =>
                  handleSelectAllChange(event.target.checked)
                }
                inputProps={{ "aria-label": "export-profiles-select-all" }}
              />
            }
            label={translate("profile:exportProfiles.options.selectAll")}
          />

          <Box sx={{ ml: 3 }}>
            <FormControlLabel
              control={<Checkbox checked disabled aria-disabled="true" />}
              label={translate(
                "profile:exportProfiles.options.profilesOnly.label"
              )}
            />
            <OptionDescription variant="caption" id="profiles-only-description">
              {translate(
                "profile:exportProfiles.options.profilesOnly.description"
              )}
            </OptionDescription>

            {optionalOptions.map((option) => (
              <Box key={option.name}>
                <Controller
                  name={option.name}
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Checkbox
                          {...field}
                          checked={field.value}
                          disabled={option.disabled}
                          onChange={(event) =>
                            field.onChange(event.target.checked)
                          }
                          inputProps={{
                            "aria-label": `export-profiles-${option.name}`,
                            "aria-describedby": `${option.name}-description`,
                          }}
                        />
                      }
                      label={option.label}
                    />
                  )}
                />
                <OptionDescription
                  variant="caption"
                  id={`${option.name}-description`}
                >
                  {option.description}
                </OptionDescription>
              </Box>
            ))}
          </Box>

          <ExportIncludesSummary
            aria-live="polite"
            aria-label="export-profiles-export-includes"
          >
            <ExportIncludesLabel>
              {translate("profile:exportProfiles.exportIncludes.label")}
            </ExportIncludesLabel>{" "}
            <ExportIncludesValue>{exportIncludesSummary}</ExportIncludesValue>
          </ExportIncludesSummary>

          <FileNameField>
            <Controller
              name="fileName"
              control={control}
              rules={{
                required: translate(
                  "reports:validationMessages.fileNameRequired"
                ),
                validate: (value) => {
                  const validationResults = validateFileName(value);
                  for (const key in validationResults) {
                    if (
                      !validationResults[key as keyof FileValidationResults]
                    ) {
                      return translate(`reports:validationMessages.${key}`);
                    }
                  }
                  return true;
                },
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={translate("reports:enterFileName")}
                  variant="outlined"
                  fullWidth
                  inputProps={{ "aria-label": "export-profiles-filename" }}
                  error={!!errors.fileName}
                  helperText={errors.fileName?.message ?? ""}
                />
              )}
            />
          </FileNameField>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            type="button"
            variant="grey"
            aria-label="export-profiles-cancel"
            onClick={handleClose}
          >
            {translate("common:cancel")}
          </Button>
          <Button
            type="submit"
            variant="success"
            aria-label="export-profiles-submit"
            disabled={!fileName?.trim() || !!errors.fileName || !teamId}
          >
            {translate("profile:exportProfiles.modal.export")}
          </Button>
        </DialogActions>
      </form>
      <BackdropLoader isOpen={isSubmitting} />
    </Dialog>
  );
};

export default ExportProfilesModal;
